import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import useBrains from '@/hooks/useBrains';
import { selectBrainId } from '@/redux/session/selectors';
import { BrainGuidelines, BrainType } from '@/shared/types/brain';

import { CharacterCountsByField, GuidelineCharacterField } from '../models';

const DEFAULT_GUIDELINE_CHAR_LIMIT = 32000;
const SOFT_WARNING_PERCENTAGE = 0.9;

type NormalisedGuidelines = Partial<Record<GuidelineCharacterField, string>>;

type UseGuidelinesCharacterCountOptions = {
  brainId?: string;
  characterLimit?: number;
  liveFieldLengths?: CharacterCountsByField;
};

const SELLER_FIELDS: readonly GuidelineCharacterField[] = Object.freeze([
  'goal',
  'overview',
  'features',
  'loyalty',
  'objections',
  'custom_instructions',
]);

const CUSTOMER_SUPPORT_FIELDS: readonly GuidelineCharacterField[] = Object.freeze([
  'objections',
  'custom_instructions',
]);

const DEBT_COLLECTION_FIELDS: readonly GuidelineCharacterField[] = Object.freeze([
  'goal',
  'objections',
  'custom_instructions',
]);

const countCharacters = (value: string): number => Array.from(value).length;

const getFieldsForBrainType = (brainType?: BrainType): readonly GuidelineCharacterField[] => {
  switch (brainType) {
    case 'support':
      return CUSTOMER_SUPPORT_FIELDS;
    case 'debt_collection':
      return DEBT_COLLECTION_FIELDS;
    default:
      return SELLER_FIELDS;
  }
};

const serialiseField = (
  field: GuidelineCharacterField,
  guidelines: BrainGuidelines | undefined
): string => {
  if (!guidelines) {
    return '';
  }

  const rawValue = (guidelines as Record<string, unknown>)[field];

  return typeof rawValue === 'string' ? rawValue : '';
};

export const useGuidelinesCharacterCount = (options: UseGuidelinesCharacterCountOptions = {}) => {
  const { brainId: explicitBrainId, characterLimit, liveFieldLengths } = options;
  const limit = characterLimit ?? DEFAULT_GUIDELINE_CHAR_LIMIT;
  const softWarningThreshold = Math.floor(limit * SOFT_WARNING_PERCENTAGE);
  const selectedBrainId = useSelector(selectBrainId);
  const brainId = explicitBrainId ?? selectedBrainId;
  const { brain } = useBrains(brainId);

  const { applicableFields, normalisedGuidelines, countsByField, totalCharacters } = useMemo(() => {
    const guidelines = brain?.guidelines;
    const fields = getFieldsForBrainType(brain?.brain_type);
    const normalised: NormalisedGuidelines = {};
    const counts: CharacterCountsByField = {};

    let total = fields.reduce((acc, field) => {
      const value = serialiseField(field, guidelines);
      normalised[field] = value;
      const baselineCount = countCharacters(value);
      const resolvedCount = liveFieldLengths?.[field] ?? baselineCount;
      counts[field] = resolvedCount;
      return acc + resolvedCount;
    }, 0);

    // Include channel guidelines in the total character count, preferring live
    // edits from the active tab over the persisted baseline.
    const CHANNEL_KEYS = ['voice', 'email', 'chat'] as const;
    const liveLengths = liveFieldLengths as Partial<Record<string, number>> | undefined;
    for (const channel of CHANNEL_KEYS) {
      const persistedValue = guidelines?.channel_guidelines?.[channel];
      const baselineCount =
        typeof persistedValue === 'string' ? countCharacters(persistedValue) : 0;
      total += liveLengths?.[channel] ?? baselineCount;
    }

    return {
      applicableFields: [...fields],
      normalisedGuidelines: normalised,
      countsByField: counts,
      totalCharacters: total,
    };
  }, [brain?.brain_type, brain?.guidelines, liveFieldLengths]);

  const remainingCharacters = limit - totalCharacters;
  const isSoftWarning = totalCharacters >= softWarningThreshold;
  const isAtLimit = totalCharacters >= limit;

  const getFieldCount = (field: GuidelineCharacterField): number => countsByField[field] ?? 0;
  const getFieldValue = (field: GuidelineCharacterField): string =>
    normalisedGuidelines[field] ?? '';

  return {
    applicableFields,
    countsByField,
    getFieldCount,
    getFieldValue,
    limit,
    normalisedGuidelines,
    remainingCharacters,
    softWarningThreshold,
    totalCharacters,
    isSoftWarning,
    isAtLimit,
  } as const;
};

export type UseGuidelinesCharacterCountReturn = ReturnType<typeof useGuidelinesCharacterCount>;
